/**
 * Asset key resolver — maps `asset_key` strings from the backend
 * (e.g. "chop-onions", "stir-sauce") to the canonical SVG icon name.
 *
 * Uses simple prefix / fuzzy matching so small variations in the backend
 * output don't break the UI. Returns `null` when no match is found,
 * so the frontend gracefully renders nothing.
 */

import type { CookingIconName } from "@/components/cooking-icons";
import { COOKING_ICON_NAMES } from "@/components/cooking-icons";

/**
 * Resolve an asset_key string to a canonical CookingIconName.
 *
 * Strategy:
 *   1. Exact match (case-insensitive) → return immediately.
 *   2. Prefix match — walks the icon names and returns the first where
 *      the asset_key starts with the canonical name (e.g. "chop-onions" → "chop").
 *   3. Substring match — if the canonical name appears anywhere in the
 *      asset_key (e.g. "season-the-meat" → "season").
 *   4. Fallback → null (render nothing).
 */
export function resolveAssetKey(
  assetKey: string | null,
): CookingIconName | null {
  if (!assetKey) return null;

  const key = assetKey.toLowerCase().trim();
  if (!key) return null;

  // 1. Exact match
  if (COOKING_ICON_NAMES.includes(key as CookingIconName)) {
    return key as CookingIconName;
  }

  // 2. Prefix match — asset_key starts with a canonical name
  for (const name of COOKING_ICON_NAMES) {
    if (key.startsWith(name + "-") || key.startsWith(name + "_")) {
      return name;
    }
  }

  // 3. Substring match — canonical name appears anywhere in the key
  for (const name of COOKING_ICON_NAMES) {
    if (key.includes(name)) {
      return name;
    }
  }

  return null;
}
